import { useEffect, useRef } from 'react';

/**
 * Replaces ScrollReveal with native IntersectionObserver.
 * Returns a ref to attach to the element you want to animate on scroll.
 *
 * @param {Object} options
 * @param {string} options.origin - direction: 'bottom', 'top', 'left', 'right'
 * @param {number} options.distance - pixels to translate (default 30)
 * @param {number} options.duration - animation duration in ms (default 1000)
 * @param {number} options.delay - delay before animation in ms (default 200)
 * @param {number} options.threshold - visibility threshold 0-1 (default 0.2)
 * @param {number} options.scale - starting scale (default 1)
 */
export function useScrollReveal({
  origin = 'bottom',
  distance = 30,
  duration = 1000,
  delay = 200,
  threshold = 0.2,
  scale = 1,
} = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Set initial hidden state
    const translateMap = {
      bottom: `translateY(${distance}px)`,
      top: `translateY(-${distance}px)`,
      left: `translateX(-${distance}px)`,
      right: `translateX(${distance}px)`,
    };

    el.style.opacity = '0';
    el.style.transform = `${translateMap[origin] || ''} scale(${scale})`;
    el.style.transition = `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0) translateX(0) scale(1)';
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [origin, distance, duration, delay, threshold, scale]);

  return ref;
}

/**
 * Observe multiple elements with a class selector.
 * Attach to a parent container ref, then all matching children get animated.
 */
export function useScrollRevealAll(selector, {
  origin = 'bottom',
  distance = 30,
  duration = 1000,
  delay = 200,
  threshold = 0.2,
  scale = 1,
  interval = 300,
} = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const elements = container.querySelectorAll(selector);
    if (!elements.length) return;

    const translateMap = {
      bottom: `translateY(${distance}px)`,
      top: `translateY(-${distance}px)`,
      left: `translateX(-${distance}px)`,
      right: `translateX(${distance}px)`,
    };

    elements.forEach((el, i) => {
      const itemDelay = delay + i * interval;
      el.style.opacity = '0';
      el.style.transform = `${translateMap[origin] || ''} scale(${scale})`;
      el.style.transition = `opacity ${duration}ms ease-out ${itemDelay}ms, transform ${duration}ms ease-out ${itemDelay}ms`;
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0) translateX(0) scale(1)';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [selector, origin, distance, duration, delay, threshold, scale, interval]);

  return containerRef;
}
